import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Play, Pause, Ban, RefreshCw, Clock, CalendarX, RotateCcw } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatNumber, formatDateTime } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { LeadStatus, MessageDispatchStatus, Tone } from '@kardume/types';
import {
  Badge,
  Button,
  Drawer,
  Modal,
  SegmentedControl,
  Select,
  Table,
  TableContainer,
  Pagination,
  StatCard,
} from '@kardume/ui';
import { useAsync } from '../../utils/useAsync';
import DataState from '../DataState';
import {
  CAMPAIGN_STATUS_LABEL,
  CAMPAIGN_STATUS_TONE,
  LEAD_STATUS_LABEL,
} from './messagingLabels';
import { describeScheduleProblem, toIsoFromInput, toLocalInputValue } from './campaignSchedule';

const HeaderBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.1rem;
`;

const Label = styled.span`
  display: block;
  margin: 1rem 0 0.4rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(p) => p.theme.textMuted};
`;

const Preview = styled.div`
  padding: 0.8rem 0.95rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.primaryLight};
  color: ${(p) => p.theme.textPrimary};
  font-size: 0.86rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Meta = styled.p`
  margin: 0 0 1rem;
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.1rem;
`;

/**
 * Motivo na linha do destinatário.
 *
 * Vermelho só quando algo deu errado. "Ignorada" é uma decisão da regra, não uma
 * falha - pintá-la de vermelho faria o operador procurar defeito onde não há.
 */
const ReasonCell = styled.span<{ $skipped?: boolean }>`
  display: block;
  font-size: 0.78rem;
  line-height: 1.4;
  color: ${(p) => (p.$skipped ? p.theme.warning : p.theme.error)};
`;

const SubText = styled.span`
  display: block;
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
`;

const MessageCell = styled.span`
  display: block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textSecondary};
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Tabs = styled.div`
  margin-bottom: 1.1rem;
`;

const ScheduleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.15rem;
`;

const DateTimeInput = styled.input`
  flex: 1;
  min-width: 190px;
  padding: 0.55rem 0.7rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surface};
  color: ${(p) => p.theme.textPrimary};
  font: inherit;
  font-size: 0.88rem;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const WarnText = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.79rem;
  line-height: 1.45;
  color: ${(p) => p.theme.warning};
`;

/**
 * Os dois cartões de configuração, lado a lado.
 *
 * Empilha abaixo de 900px porque o drawer encolhe junto com a viewport: em duas
 * colunas estreitas o rótulo e o valor de cada linha quebram um sobre o outro e a
 * ficha para de ser legível.
 */
const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/** Título do cartão. Igual ao [Label], mas sem a margem de topo que separa seções. */
const CardTitle = styled.span`
  display: block;
  margin: 0 0 0.45rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(p) => p.theme.textMuted};
`;

/** Ficha de configuração: rótulo à esquerda, valor à direita, uma linha por dado. */
const InfoList = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr);
  gap: 0.55rem 1rem;
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.5;

  dt {
    color: ${(p) => p.theme.textMuted};
    font-weight: 600;
  }

  dd {
    margin: 0;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
`;

const CardBox = styled.div`
  padding: 0.95rem 1.05rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surface};
`;

const DISPATCH_STATUS_TONE: Record<MessageDispatchStatus, Tone> = {
  PENDING: 'secondary',
  SENDING: 'info',
  SENT: 'success',
  FAILED: 'danger',
  SKIPPED: 'warning',
  CANCELED: 'secondary',
};

const DISPATCH_STATUS_LABEL: Record<MessageDispatchStatus, string> = {
  PENDING: 'Na fila',
  SENDING: 'Enviando',
  SENT: 'Enviada',
  FAILED: 'Falhou',
  SKIPPED: 'Ignorada',
  CANCELED: 'Cancelada',
};

const DISPATCH_FILTER = [
  { value: '', label: 'Todos os destinatários' },
  ...(Object.keys(DISPATCH_STATUS_LABEL) as MessageDispatchStatus[]).map((status) => ({
    value: status,
    label: DISPATCH_STATUS_LABEL[status],
  })),
];

/** Enquanto a campanha drena, o drawer se atualiza sozinho. */
const RUNNING_POLL_MS = 15000;

type DetailTab = 'recipients' | 'settings';

const DETAIL_TABS: { value: DetailTab; label: string }[] = [
  { value: 'recipients', label: 'Destinatários' },
  { value: 'settings', label: 'Configuração' },
];

const AUDIENCE_LABEL: Record<string, string> = {
  CONTACT_LIST: 'Lista de contatos',
  LEADS: 'Leads',
};

/** `HH:mm:ss` do backend vira `HH:mm` na tela, como na aba de instâncias. */
const toShortTime = (value: string): string => value.slice(0, 5);

interface CampaignDetailDrawerProps {
  campaignId: string | null;
  canSend: boolean;
  onClose: () => void;
  /** Avisa a listagem de fundo que os contadores mudaram. */
  onChanged: () => void;
}

/**
 * Acompanhamento de uma campanha, em drawer.
 *
 * Drawer e não navegação: a listagem continua montada atrás, então fechar volta
 * exatamente para a página e o filtro em que o operador estava - e ele consegue
 * abrir uma campanha, conferir e voltar sem perder o contexto.
 */
const CampaignDetailDrawer: React.FC<CampaignDetailDrawerProps> = ({
  campaignId,
  canSend,
  onClose,
  onChanged,
}) => {
  const { showNotification } = useNotification();
  const [acting, setActing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  /**
   * Rascunho do campo de data, carregando o id da campanha a que pertence.
   *
   * Mesmo motivo do `view` abaixo: sem o id, abrir outra campanha traria a data
   * digitada na anterior já no campo, pronta para ser salva na errada.
   */
  const [scheduleDraft, setScheduleDraft] = useState<{ id: string | null; value: string } | null>(null);
  const [savingSchedule, setSavingSchedule] = useState(false);

  /**
   * Filtro e página carregam o id da campanha a que pertencem.
   *
   * Zerar por `useEffect` não bastava: o efeito roda **depois** do fetch, então
   * abrir a campanha B logo após ter filtrado "Falhou" na página 3 da campanha A
   * disparava primeiro `GET .../B/dispatches?page=2&status=SENT` e só depois o
   * pedido correto - e, sem guarda de corrida, o primeiro podia chegar por último.
   */
  const [view, setView] = useState<{
    id: string | null;
    filter: string;
    page: number;
    tab: DetailTab;
  }>({ id: campaignId, filter: '', page: 0, tab: 'recipients' });
  // A aba mora no mesmo estado que o filtro justamente para herdar o reset: abrir
  // outra campanha volta para "Destinatários", em vez de cair na configuração da
  // campanha anterior ainda selecionada.
  const current =
    view.id === campaignId ? view : { id: campaignId, filter: '', page: 0, tab: 'recipients' as DetailTab };
  const dispatchFilter = current.filter;
  const dispatchPage = current.page;
  const tab = current.tab;
  const setDispatchFilter = (filter: string): void =>
    setView({ id: campaignId, filter, page: 0, tab: current.tab });
  const setDispatchPage = (page: number): void =>
    setView({ id: campaignId, filter: current.filter, page, tab: current.tab });
  const setTab = (next: DetailTab): void =>
    setView({ id: campaignId, filter: current.filter, page: current.page, tab: next });

  const detail = useAsync(
    () => (campaignId ? backofficeApi.getMessageCampaign(campaignId) : Promise.resolve(null)),
    [campaignId]
  );

  const dispatches = useAsync(
    () =>
      campaignId
        ? backofficeApi.listMessageCampaignDispatches(campaignId, {
            page: dispatchPage,
            size: 20,
            ...(dispatchFilter ? { status: dispatchFilter as MessageDispatchStatus } : {}),
          })
        : Promise.resolve(null),
    [campaignId, dispatchPage, dispatchFilter]
  );

  const campaign = detail.data;
  const reloadDetail = detail.reload;
  const reloadDispatches = dispatches.reload;

  /**
   * Limites da instância, buscados só quando a aba de configuração abre.
   *
   * É o dado que responde "por que ainda não enviou": teto diário, intervalo e
   * janela de horário moram na instância, não na campanha. Carregar sob demanda
   * evita uma requisição a mais em quem só quer acompanhar a fila - e a falha aqui
   * é silenciosa de propósito: o bloco some, mas o acompanhamento continua.
   */
  const instanceId = campaign?.instanceId ?? null;
  const instances = useAsync(
    () =>
      tab === 'settings' && instanceId
        ? backofficeApi.listMessagingInstances()
        : Promise.resolve(null),
    [tab, instanceId]
  );
  const instance = instances.data?.find((item) => item.id === instanceId) ?? null;
  const reloadInstances = instances.reload;

  const scheduleValue =
    scheduleDraft && scheduleDraft.id === campaignId
      ? scheduleDraft.value
      : toLocalInputValue(campaign?.scheduledAt);
  const scheduleProblem = describeScheduleProblem(scheduleValue, instance);

  const saveSchedule = async (iso: string | null): Promise<void> => {
    if (!campaignId) return;
    setSavingSchedule(true);
    try {
      await backofficeApi.scheduleMessageCampaign(campaignId, iso);
      showNotification(iso ? 'Início agendado.' : 'Agendamento removido.', 'success');
      // Descarta o rascunho para o campo voltar a refletir o que o servidor gravou.
      setScheduleDraft(null);
      reloadDetail();
      onChanged();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível agendar a campanha.'), 'error');
    } finally {
      setSavingSchedule(false);
    }
  };

  useEffect(() => {
    if (campaign?.status !== 'RUNNING') return undefined;
    const timer = setInterval(() => {
      reloadDetail();
      reloadDispatches();
      // "Enviadas hoje" avança a cada envio: sem isto o número da aba de
      // configuração congelaria no valor de quando a aba foi aberta.
      reloadInstances();
    }, RUNNING_POLL_MS);
    return () => clearInterval(timer);
  }, [campaign?.status, reloadDetail, reloadDispatches, reloadInstances]);

  const act = async (
    action: 'start' | 'pause' | 'cancel' | 'retry',
    message: string
  ): Promise<void> => {
    if (!campaignId) return;
    setActing(true);
    try {
      if (action === 'start') await backofficeApi.startMessageCampaign(campaignId);
      if (action === 'pause') await backofficeApi.pauseMessageCampaign(campaignId);
      if (action === 'cancel') await backofficeApi.cancelMessageCampaign(campaignId);
      if (action === 'retry') await backofficeApi.retryFailedMessageCampaign(campaignId);
      showNotification(message, 'success');
      setConfirmCancel(false);
      reloadDetail();
      reloadDispatches();
      onChanged();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível concluir a ação.'), 'error');
    } finally {
      setActing(false);
    }
  };

  const page = dispatches.data;
  const rows = page?.content ?? [];

  return (
    <>
      <Drawer
        isOpen={campaignId !== null}
        onClose={onClose}
        title={campaign?.name ?? 'Campanha'}
        width="760px"
        headerActions={
          campaign ? (
            <HeaderBadges>
              <Badge tone={CAMPAIGN_STATUS_TONE[campaign.status]}>
                {CAMPAIGN_STATUS_LABEL[campaign.status]}
              </Badge>
              {/* O recorte fica à vista porque a conferência acontece meses depois, quando a
                  parceria daqueles lojistas já pode ter mudado - sem isso ninguém explica
                  por que eles não saíram na fila. */}
              {campaign.excludePartners && (
                <span title="Parceiros ativos entraram na campanha como ignorados">
                  <Badge tone="primary">sem parceiros</Badge>
                </span>
              )}
            </HeaderBadges>
          ) : undefined
        }
      >
        {/* `&& !campaign`: o polling de 15s trocaria o conteúdo inteiro por
            esqueletos - inclusive os botões Pausar e Cancelar, na tela cujo
            trabalho é justamente interromper um envio em andamento. */}
        <DataState loading={detail.loading && !campaign} error={detail.error} onRetry={reloadDetail}>
          {campaign && (
            <>
              <Actions>
                {canSend && (campaign.status === 'DRAFT' || campaign.status === 'PAUSED') && (
                  <Button
                    variant="primary"
                    onClick={() => act('start', 'Campanha iniciada. O envio segue o ritmo da instância.')}
                    isLoading={acting}
                  >
                    <Play size={15} /> {campaign.status === 'DRAFT' ? 'Iniciar' : 'Retomar'}
                  </Button>
                )}
                {canSend && campaign.status === 'RUNNING' && (
                  <Button
                    variant="outline"
                    onClick={() => act('pause', 'Campanha pausada.')}
                    isLoading={acting}
                  >
                    <Pause size={15} /> Pausar
                  </Button>
                )}
                {/* Só aparece quando há o que reenviar: um botão sempre visível que
                    responde "não há falhas" é ruído em toda campanha saudável. */}
                {canSend && campaign.status !== 'CANCELED' && campaign.counters.failed > 0 && (
                  <Button
                    variant="outline"
                    onClick={() =>
                      act(
                        'retry',
                        'Destinatários com falha voltaram para a fila. Eles seguem o ritmo e a cota da instância.'
                      )
                    }
                    isLoading={acting}
                  >
                    <RotateCcw size={15} /> Reenviar {formatNumber(campaign.counters.failed)} falha(s)
                  </Button>
                )}
                {canSend && campaign.status !== 'COMPLETED' && campaign.status !== 'CANCELED' && (
                  <Button variant="danger" onClick={() => setConfirmCancel(true)} disabled={acting}>
                    <Ban size={15} /> Cancelar
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={() => {
                    reloadDetail();
                    reloadDispatches();
                    reloadInstances();
                  }}
                >
                  <RefreshCw size={15} /> Atualizar
                </Button>
              </Actions>

              <Tabs>
                <SegmentedControl options={DETAIL_TABS} value={tab} onChange={setTab} />
              </Tabs>

              {tab === 'recipients' && (
                <>
                  <StatsGrid>
                    <StatCard label="Na fila" value={formatNumber(campaign.counters.pending)} />
                    <StatCard label="Enviadas" value={formatNumber(campaign.counters.sent)} />
                    <StatCard label="Falharam" value={formatNumber(campaign.counters.failed)} />
                    {/* Sem "(opt-out)" no rótulo: agora também caem aqui os
                        contatos que já receberam este template. O motivo de cada um
                        aparece na linha do destinatário. */}
                    <StatCard label="Ignoradas" value={formatNumber(campaign.counters.skipped)} />
                  </StatsGrid>

                  {campaign.estimatedMinutesRemaining ? (
                    <Meta>
                      Tempo restante estimado: {formatNumber(campaign.estimatedMinutesRemaining)} min
                      (piso - não conta as pausas do teto diário nem da janela de horário).
                    </Meta>
                  ) : null}

                  <Select
                    options={DISPATCH_FILTER}
                    value={dispatchFilter}
                    onChange={(e) => {
                      setDispatchFilter(e.target.value);
                    }}
                  />

                  <div style={{ marginTop: '0.75rem' }}>
                    <DataState
                      loading={dispatches.loading && !page}
                      error={dispatches.error}
                      isEmpty={rows.length === 0}
                      emptyTitle="Nenhum destinatário neste filtro"
                      onRetry={reloadDispatches}
                    >
                      <TableContainer>
                        <Table>
                          <thead>
                            <tr>
                              <th>Destinatário</th>
                              <th>Mensagem</th>
                              <th style={{ whiteSpace: 'nowrap' }}>Enviada em</th>
                              <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((dispatch) => (
                              <tr key={dispatch.id}>
                                <td>
                                  <strong>{dispatch.recipientName}</strong>
                                  <SubText>{dispatch.phoneNumberFormatted}</SubText>
                                  {dispatch.lastError && (
                                    <ReasonCell $skipped={dispatch.status === 'SKIPPED'}>
                                      {dispatch.lastError}
                                    </ReasonCell>
                                  )}
                                </td>
                                <td>
                                  <MessageCell title={dispatch.renderedBody}>
                                    {dispatch.renderedBody}
                                  </MessageCell>
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {dispatch.sentAt ? formatDateTime(dispatch.sentAt) : '-'}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  <Badge tone={DISPATCH_STATUS_TONE[dispatch.status]}>
                                    {DISPATCH_STATUS_LABEL[dispatch.status]}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableContainer>
                    </DataState>
                  </div>

                  {(page?.totalPages ?? 0) > 1 && (
                    <Pagination
                      currentPage={dispatchPage + 1}
                      totalPages={page?.totalPages ?? 1}
                      totalItems={page?.totalElements}
                      pageSize={20}
                      disabled={dispatches.loading}
                      onPageChange={(next) => setDispatchPage(next - 1)}
                    />
                  )}
                </>
              )}

              {tab === 'settings' && (
                <>
                  <SettingsGrid>
                    <div>
                      <CardTitle>Campanha</CardTitle>
                      <CardBox>
                        <InfoList>
                          <dt>Nome</dt>
                          <dd>{campaign.name}</dd>

                          <dt>Situação</dt>
                          <dd>{CAMPAIGN_STATUS_LABEL[campaign.status]}</dd>

                          <dt>Audiência</dt>
                          <dd>{AUDIENCE_LABEL[campaign.audienceType] ?? campaign.audienceType}</dd>

                          {campaign.contactListName ? (
                            <>
                              <dt>Lista</dt>
                              <dd>{campaign.contactListName}</dd>
                            </>
                          ) : null}

                          {campaign.leadStatuses.length > 0 ? (
                            <>
                              <dt>Colunas do funil</dt>
                              <dd>
                                {campaign.leadStatuses
                                  .map((s: LeadStatus) => LEAD_STATUS_LABEL[s])
                                  .join(', ')}
                              </dd>
                            </>
                          ) : null}

                          <dt>Parceiros</dt>
                          <dd>
                            {campaign.excludePartners
                              ? 'ignorados (parceria ativa)'
                              : 'incluídos na fila'}
                          </dd>

                          <dt>Destinatários</dt>
                          <dd>{formatNumber(campaign.counters.total)}</dd>

                          <dt>Criada em</dt>
                          <dd>{formatDateTime(campaign.createdAt)}</dd>

                          <dt>Iniciada em</dt>
                          <dd>{campaign.startedAt ? formatDateTime(campaign.startedAt) : '-'}</dd>

                          <dt>Concluída em</dt>
                          <dd>
                            {campaign.completedAt ? formatDateTime(campaign.completedAt) : '-'}
                          </dd>

                          <dt>Início automático</dt>
                          <dd>
                            {campaign.scheduledAt
                              ? formatDateTime(campaign.scheduledAt)
                              : 'manual'}
                          </dd>
                        </InfoList>
                      </CardBox>
                    </div>

                    <div>
                      <CardTitle>Número que envia</CardTitle>
                      <CardBox>
                        <InfoList>
                          <dt>Instância</dt>
                          <dd>{campaign.instanceName}</dd>

                          {instance ? (
                            <>
                              <dt>Número</dt>
                              <dd>{instance.phoneNumber || 'ainda não pareado'}</dd>

                              <dt>Enviadas hoje</dt>
                              <dd>
                                {formatNumber(instance.sentToday)} de{' '}
                                {formatNumber(instance.dailyLimit)}
                              </dd>

                              <dt>Intervalo</dt>
                              <dd>
                                {instance.minIntervalSeconds}–{instance.maxIntervalSeconds}s
                                (sorteado a cada mensagem)
                              </dd>

                              <dt>Janela</dt>
                              <dd>
                                {toShortTime(instance.windowStart)}–
                                {toShortTime(instance.windowEnd)}
                                {instance.sendOnWeekends
                                  ? ', inclusive fins de semana'
                                  : ', apenas em dias úteis'}
                              </dd>
                            </>
                          ) : null}
                        </InfoList>
                      </CardBox>
                    </div>
                  </SettingsGrid>

                  {canSend && (campaign.status === 'DRAFT' || campaign.status === 'PAUSED') && (
                    <>
                      <Label>Início automático</Label>
                      <CardBox>
                        <ScheduleRow>
                          <DateTimeInput
                            type="datetime-local"
                            value={scheduleValue}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setScheduleDraft({ id: campaignId, value: e.target.value })
                            }
                          />
                          <Button
                            variant="primary"
                            onClick={() => saveSchedule(toIsoFromInput(scheduleValue))}
                            isLoading={savingSchedule}
                            disabled={!scheduleValue || !!scheduleProblem}
                          >
                            <Clock size={15} /> Agendar
                          </Button>
                          {campaign.scheduledAt && (
                            <Button
                              variant="ghost"
                              onClick={() => saveSchedule(null)}
                              disabled={savingSchedule}
                            >
                              <CalendarX size={15} /> Remover
                            </Button>
                          )}
                        </ScheduleRow>
                        {scheduleProblem ? (
                          <WarnText>{scheduleProblem}</WarnText>
                        ) : (
                          <Meta style={{ margin: '0.45rem 0 0' }}>
                            {campaign.scheduledAt
                              ? `Agendada para ${formatDateTime(campaign.scheduledAt)}. Nada sai antes disso.`
                              : 'Sem data, a campanha espera alguém apertar "Iniciar".'}
                          </Meta>
                        )}
                      </CardBox>
                    </>
                  )}

                  <Label>Mensagem desta campanha</Label>
                  <Preview>{campaign.templateBody}</Preview>
                  <Meta style={{ marginTop: '0.5rem' }}>
                    Texto congelado quando a campanha foi criada - editar o template depois não
                    reescreve o que já está na fila.
                  </Meta>
                </>
              )}
            </>
          )}
        </DataState>
      </Drawer>

      <Modal
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        title="Cancelar campanha"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmCancel(false)} disabled={acting}>
              Voltar
            </Button>
            <Button variant="danger" onClick={() => act('cancel', 'Campanha cancelada.')} isLoading={acting}>
              Cancelar campanha
            </Button>
          </>
        }
      >
        <ConfirmText>
          Cancelar <strong>{campaign?.name}</strong> descarta os{' '}
          {formatNumber(campaign?.counters.pending ?? 0)} destinatário(s) que ainda estão na fila. É
          irreversível - o que já foi enviado não é afetado.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default CampaignDetailDrawer;
